import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BookOpen, Clock, Layers3, Loader2, Search } from 'lucide-react';
import { RequestHMSupport } from '@/components/shared/RequestHMSupport';
import { ClientSelfServeAssignDialog } from '@/components/client/ClientSelfServeAssignDialog';
import { ListGridToggle } from '@/components/shared/ListGridToggle';

const SUITE_BRAND = {
  talentmap: 'TalentMap', talentcheck: 'TalentCheck', talentstart: 'TalentStart',
  talentheart: 'TalentHeart', talentfix: 'TalentFix', talentcompetency: 'TalentCompetency',
  talenttrain: 'TalentTrain', talentexit: 'TalentExit', talentperformance: 'TalentPerformance',
  intelligence: 'TalentIntelligence', climara: 'Climara', succession: 'Succession',
  global_ethics: 'Global Ethics',
};
const brand = (s) => SUITE_BRAND[s] || s;
const locName = (x, isES) => (isES ? (x.name_es || x.name) : x.name);
const locDesc = (x, isES) => (isES
  ? (x.description_es || x.description)
  : (x.description_en || x.description)) || '—';

export default function ClientLibrary() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [items, setItems] = useState([]);
  const [assignFrom, setAssignFrom] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [view, setView] = useState('grid');
  const [suite, setSuite] = useState('');
  const [detail, setDetail] = useState(null);

  const load = async (params = {}) => {
    setLoading(true);
    try {
      const r = await api.get('/client/library', { params });
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const suites = [...new Set(items.flatMap((x) => x.suite_codes || []))];

  const openDetail = async (id) => {
    try { const r = await api.get(`/client/library/${id}`); setDetail(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = suite ? items.filter((x) => (x.suite_codes || []).includes(suite)) : items;

  return (
    <>
      <Topbar
        title={L('Biblioteca de Evaluaciones', 'Assessment Library')}
        subtitle={L('Consuma instrumentos aprobados de CTH — la creación y metodología permanecen gobernadas por el equipo CTH',
          'Consume approved CTH instruments — creation and methodology remain governed by the CTH team')}
      />
      <div className="p-6 lg:p-8 space-y-4" data-testid="client-library-page">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-72">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input className="pl-8" placeholder={L('Buscar evaluaciones…', 'Search assessments…')}
                   value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === 'Enter' && load({ q })}
                   data-testid="library-search-input" />
          </div>
          <Button size="sm" variant="secondary" onClick={() => load({ q })} data-testid="library-search-btn">
            {L('Buscar', 'Search')}
          </Button>
          <div className="flex gap-1.5 flex-wrap">
            <button type="button" onClick={() => setSuite('')}
                    className={`h-7 px-2.5 rounded-full border text-xs ${!suite ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>
              {L('Todas', 'All')}
            </button>
            {suites.map((s) => (
              <button key={s} type="button" onClick={() => setSuite(s)}
                      data-testid={`library-suite-${s}`}
                      className={`h-7 px-2.5 rounded-full border text-xs ${suite === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground'}`}>
                {brand(s)}
              </button>
            ))}
          </div>
          <div className="ml-auto">
            <ListGridToggle value={view} onChange={setView} testIdPrefix="library-view" />
            <RequestHMSupport suite="library" defaultCategory="new_instrument" />
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="library-empty">
            {L('No hay evaluaciones publicadas para sus suites contratadas. Puede solicitar una nueva evaluación al equipo CTH.',
               'No published assessments for your contracted suites. You can request a new assessment from the CTH team.')}
          </Card>
        ) : view === 'list' ? (
          <Card className="shadow-card divide-y" data-testid="library-list">
            {filtered.map((x) => (
              <div key={x.id} className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40"
                   onClick={() => openDetail(x.id)} data-testid={`library-row-${x.id}`}>
                <BookOpen className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm">{locName(x, isES)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{locDesc(x, isES)}</p>
                </div>
                <div className="flex flex-wrap gap-1 shrink-0">
                  {(x.suite_codes || []).map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px]">{brand(s)}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((x) => (
              <Card key={x.id} className="p-4 shadow-card hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openDetail(x.id)} data-testid={`library-card-${x.id}`}>
                <div className="flex items-start gap-2">
                  <BookOpen className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm line-clamp-2">{locName(x, isES)}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{locDesc(x, isES)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  {(x.suite_codes || []).map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px]">{brand(s)}</Badge>
                  ))}
                  {x.evaluation_type && <Badge variant="outline" className="text-[10px]">{x.evaluation_type}</Badge>}
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            {detail && (
              <>
                <DialogHeader>
                  <DialogTitle>{locName(detail, isES)}</DialogTitle>
                  <DialogDescription>{(isES ? (detail.description_es || detail.description) : (detail.description_en || detail.description)) || detail.objective || ''}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 text-sm" data-testid="library-detail">
                  <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Layers3 className="h-3.5 w-3.5" />
                      {detail.dimension_count} {L('dimensiones', 'dimensions')} · {detail.question_count} {L('reactivos', 'items')}
                    </span>
                    {detail.estimated_duration_minutes && (
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />
                        ~{detail.estimated_duration_minutes} min
                      </span>
                    )}
                    <span className="capitalize">{(detail.suite_codes || []).join(' · ')}</span>
                  </div>
                  {(detail.dimensions || []).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                        {L('Dimensiones evaluadas', 'Measured dimensions')}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {detail.dimensions.map((d, i) => <Badge key={i} variant="outline" className="text-[10px]">{d}</Badge>)}
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground italic">
                    {L('Los instrumentos, preguntas y calificación permanecen gobernados por el equipo CTH. Puedes asignar las evaluaciones publicadas directamente o desde el flujo del módulo correspondiente.',
                       'Instruments, questions and scoring remain governed by the CTH team. You can assign published assessments directly or from the relevant module workflow.')}
                  </p>
                  {/* Client Autonomy — consumption path driven by EFFECTIVE
                       suite eligibility (suite_codes + active configuration). */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {(() => {
                      const eff = detail.effective_suite_codes || detail.suite_codes || [];
                      const routes = {
                        talentcheck: ['/client/talentcheck', 'TalentCheck'],
                        talentcompetency: ['/client/talentcompetency', 'TalentCompetency'],
                        talenttrain: ['/client/talenttrain', 'TalentTrain'],
                        climara: ['/client/climara', 'Climara'],
                        talentexit: ['/client/talentexit', 'TalentExit'],
                        talentperformance: ['/client/talentperformance', 'TalentPerformance'],
                        talentstart: ['/client/talentstart', 'TalentStart'],
                        talentfix: ['/client/talentfix', 'TalentFix'],
                        talentheart: ['/client/talentheart', 'TalentHeart'],
                        succession: ['/client/succession', 'Succession'],
                        intelligence: ['/client/talentintelligence', 'TalentIntelligence'],
                      };
                      const btns = [];
                      if (eff.includes('talentmap')) {
                        btns.push(
                          <Button key="assign" size="sm" className="gap-1.5" data-testid="library-assign-btn"
                                  onClick={() => setAssignFrom(detail)}>
                            {L('Asignar evaluación', 'Assign evaluation')}
                          </Button>,
                        );
                      }
                      eff.filter((s) => routes[s]).slice(0, 4).forEach((s) => {
                        btns.push(
                          <Button key={s} size="sm" variant="outline" data-testid={`library-goto-${s}`}
                                  onClick={() => navigate(routes[s][0])}>
                            {L(`Administrar desde ${routes[s][1]}`, `Manage from ${routes[s][1]}`)}
                          </Button>,
                        );
                      });
                      return btns;
                    })()}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <ClientSelfServeAssignDialog
          open={!!assignFrom}
          onOpenChange={(v) => !v && setAssignFrom(null)}
          suite="talentmap"
          presetInstrument={assignFrom}
        />
      </div>
    </>
  );
}
